/**
 * WHERE A MISSED BLAST ACTUALLY LANDS.
 *
 * A thrown or fired blast that misses does not vanish - it goes somewhere,
 * and where it goes is the difference between a wasted grenade and one at
 * your own feet. Deathwatch resolves that with a clock face for the direction
 * and a distance that grows with how badly the shot was thrown.
 *
 * TWO ROLLS, NO MORE, AND BOTH THROUGH THE DICE SYSTEM. Every seeded E2E
 * sequence downstream of an attack shifts if the engine consumes randomness
 * some other way, or a different number of times - gotcha #5. `rollDie` is
 * injected for exactly that reason: the tests here supply their own, and the
 * app supplies the one the seeded runs stub.
 *
 * A MELEE BLAST NEVER SCATTERS (user, 2026-08-17). A swing that misses simply
 * misses; there is no projectile to go astray. That rule lives at the caller,
 * because it is about the weapon rather than about the dice.
 *
 * Pure, zero React dependency.
 */

/** A roll of one die of N sides. Injected so tests need no seeding. */
export type DieRoller = (sides: number) => number;

export interface ScatterResult {
  /**
   * The clock face, 1–10.
   *
   * 1 is directly AWAY from the thrower - the natural overthrow - and the
   * rest run clockwise in 36° steps. A d10 rather than a d12 because that is
   * the die this system actually has on the table.
   */
  clock: number;
  /** Degrees clockwise from "straight on past the target". */
  offsetDegrees: number;
  /** How far it went, in metres. */
  metres: number;
  /** The raw distance die, for the log. */
  distanceRoll: number;
}

/** 360 / 10 - one clock step. */
const STEP_DEGREES = 36;

/**
 * Roll where a missed blast went.
 *
 * Distance is one d5 multiplied by the degrees of failure, not a d5 per
 * degree: one roll keeps the seeded sequences short and predictable, and the
 * spread is the same shape. A degree of failure of zero is treated as one -
 * a miss by a hair still has to land somewhere other than the target.
 */
export function rollScatter(dof: number, rollDie: DieRoller): ScatterResult {
  const clock = rollDie(10);
  const distanceRoll = rollDie(5);
  const degrees = Math.max(1, Math.floor(dof) || 1);
  return {
    clock,
    offsetDegrees: (clock - 1) * STEP_DEGREES,
    metres: distanceRoll * degrees,
    distanceRoll,
  };
}

/** One line for the combat log, in the language the table uses. */
export function scatterNote(result: ScatterResult): string {
  return `Scatter: ${result.clock} o'clock, ${result.metres} m (1d5 = ${result.distanceRoll})`;
}
